/**
 * The initials an avatar draws when it has no picture (avatar 2.3): the first letter of the first
 * and the last word, or the first two letters of a single word, uppercased. A letter is a whole
 * grapheme, so an accent stays with its letter and an emoji is never split into broken halves;
 * a word with no letter or digit (an emoji, a dash) is skipped, and so is a legal form at the end.
 */
/** Legal forms at the end of a business name are not part of its initials ("Mikathletic SASU" is MI). */
const LEGAL_FORMS = new Set(["ei", "eirl", "eurl", "sarl", "sas", "sasu", "sa", "sci", "ltd", "llc", "inc", "gmbh", "ug", "srl", "bv"]);

/** The first letter of a word as a reader sees it: one grapheme, letters and digits only, so an emoji is skipped. */
function firstLetter(word: string): string | undefined {
  const graphemes = [...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(word)];
  return graphemes.map((part) => part.segment).find((part) => /^[\p{L}\p{N}]/u.test(part));
}

export function initialsOf(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => firstLetter(word) !== undefined);
  const meaningful =
    words.length > 1 && LEGAL_FORMS.has(words[words.length - 1].toLocaleLowerCase())
      ? words.slice(0, -1)
      : words;
  if (meaningful.length === 0) return "";
  if (meaningful.length === 1) {
    const letters = [...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(meaningful[0])]
      .map((part) => part.segment)
      .filter((part) => /^[\p{L}\p{N}]/u.test(part));
    return letters.slice(0, 2).join("").toLocaleUpperCase();
  }
  const first = firstLetter(meaningful[0]) ?? "";
  const last = firstLetter(meaningful[meaningful.length - 1]) ?? "";
  return (first + last).toLocaleUpperCase();
}
